import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Save, Send, LogOut, ExternalLink, Sparkles, Coins, Info } from "lucide-react";
import SafeEmail from "@/components/SafeEmail";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cercle-local-bhealth/artifacts/csfd9ojj_logofinal-logo-full-color-rgb-900px-w-72ppi.png";
const CATEGORIES = ["Food", "Santé", "Sport", "Bien-être", "Service", "Famille"];

export default function MerchantDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/partenaire/login", { replace: true }); return; }
    if (user.role !== "merchant") { nav("/admin", { replace: true }); return; }
    (async () => {
      try { setPartner((await api.get("/merchant/partner")).data); }
      catch { /* no partner yet */ }
    })();
  }, [user, loading, nav]);

  const set = (k, v) => setPartner((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/merchant/partner", {
        name: partner.name, category: partner.category,
        description: partner.description, long_description: partner.long_description,
        offer: partner.offer, offer_value: parseFloat(partner.offer_value) || 0,
        address: partner.address, city: partner.city,
        phone: partner.phone, email: partner.email,
        website: partner.website, instagram: partner.instagram, facebook: partner.facebook,
        conditions: partner.conditions, image_url: partner.image_url,
        start_date: partner.start_date || null, end_date: partner.end_date || null,
      });
      setPartner(data);
      toast.success("Enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const submitForReview = async () => {
    try {
      await api.post("/merchant/submit");
      toast.success("Fiche transmise pour validation");
      setPartner((await api.get("/merchant/partner")).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/merchant/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image ajoutée");
    } catch { toast.error("Upload impossible"); }
    finally { setUploading(false); }
  };

  if (loading || !partner) {
    return <div className="min-h-screen grid place-items-center bg-muted"><div className="w-10 h-10 rounded-full border-2 border-navy-900 border-t-transparent animate-spin" /></div>;
  }

  const statusMeta = {
    draft: { label: "Brouillon — complétez puis soumettez", tone: "bg-amber-100 text-amber-900" },
    pending: { label: "En attente de validation", tone: "bg-blue-100 text-blue-900" },
    active: { label: "En ligne — visible sur le site", tone: "bg-lime-500/20 text-navy-900" },
    inactive: { label: "Hors ligne", tone: "bg-muted text-navy-900/70" },
    expired: { label: "Expirée", tone: "bg-muted text-navy-900/70" },
  }[partner.status] || { label: partner.status, tone: "bg-muted" };

  return (
    <div className="min-h-screen bg-muted">
      <header className="bg-navy-900 text-white">
        <div className="container-app flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white p-1 flex items-center justify-center">
              <img src={LOGO_URL} alt="B.health®" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-extrabold uppercase tracking-wider text-sm">Espace partenaire</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-semibold">B.health® Local Benefits</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-white/70">{user.email}</span>
            <a href="/" className="text-xs text-white/60 hover:text-lime-400 inline-flex items-center gap-1" target="_blank" rel="noreferrer"><ExternalLink className="w-3 h-3" /> Site</a>
            <button onClick={() => { logout(); nav("/partenaire/login"); }} className="text-xs text-white/60 hover:text-lime-400 inline-flex items-center gap-1" data-testid="merchant-logout"><LogOut className="w-3 h-3" /> Déconnexion</button>
          </div>
        </div>
      </header>

      <main className="container-app py-8 md:py-12 space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-navy-900/60 font-semibold">Bienvenue</div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy-900 tracking-tighter mt-1" data-testid="merchant-dashboard-title">{partner.name || "Votre fiche"}</h1>
            <span className={`mt-3 inline-block chip ${statusMeta.tone}`}>{statusMeta.label}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="btn-ghost text-sm" data-testid="merchant-save-btn"><Save className="w-4 h-4" /> {busy ? "Enregistrement…" : "Enregistrer"}</button>
            {partner.status !== "active" && (
              <button onClick={submitForReview} className="btn-lime text-sm" data-testid="merchant-submit-btn"><Send className="w-4 h-4" /> Soumettre pour validation</button>
            )}
          </div>
        </div>

        {/* Billing notice — priority display */}
        <PricingBanner />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left : info + offer */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Informations du commerce">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nom du commerce *"><Input value={partner.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="m-name" /></Field>
                <Field label="Catégorie *">
                  <Select value={partner.category || ""} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="m-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Description courte *" className="md:col-span-2">
                  <Input value={partner.description || ""} maxLength={140} onChange={(e) => set("description", e.target.value)} data-testid="m-description" />
                </Field>
                <Field label="Description longue" className="md:col-span-2">
                  <Textarea rows={3} value={partner.long_description || ""} onChange={(e) => set("long_description", e.target.value)} data-testid="m-long-desc" />
                </Field>
                <Field label="Adresse *" className="md:col-span-2"><Input value={partner.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="m-address" /></Field>
                <Field label="Ville"><Input value={partner.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
                <Field label="Téléphone"><Input value={partner.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Email de contact"><Input value={partner.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
                <Field label="Site web"><Input value={partner.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
                <Field label="Instagram"><Input value={partner.instagram || ""} onChange={(e) => set("instagram", e.target.value)} /></Field>
                <Field label="Facebook"><Input value={partner.facebook || ""} onChange={(e) => set("facebook", e.target.value)} /></Field>
              </div>
            </Card>

            <Card title={<span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-lime-600" /> Votre offre privilégiée</span>}>
              <div className="space-y-4">
                <Field label="Libellé de l'offre * (mettez en avant la valeur en €)">
                  <Input value={partner.offer || ""} onChange={(e) => set("offer", e.target.value)} placeholder="Ex : -10€ sur tout achat ≥ 100€" data-testid="m-offer" />
                </Field>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Valeur estimée par utilisation (€)">
                    <Input type="number" step="0.5" value={partner.offer_value || 0} onChange={(e) => set("offer_value", e.target.value)} data-testid="m-offer-value" />
                  </Field>
                  <Field label="Conditions particulières">
                    <Input value={partner.conditions || ""} onChange={(e) => set("conditions", e.target.value)} />
                  </Field>
                  <Field label="Date de début"><Input type="date" value={(partner.start_date || "").slice(0,10)} onChange={(e) => set("start_date", e.target.value)} /></Field>
                  <Field label="Date de fin"><Input type="date" value={(partner.end_date || "").slice(0,10)} onChange={(e) => set("end_date", e.target.value)} /></Field>
                </div>
              </div>
            </Card>
          </div>

          {/* Right : image + summary */}
          <div className="space-y-6">
            <Card title="Photo de couverture">
              <div className="space-y-3">
                {partner.image_url ? (
                  <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-muted">
                    <img src={resolveImageUrl(partner.image_url)} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="rounded-xl aspect-[4/3] bg-muted border-2 border-dashed border-navy-900/10 flex items-center justify-center text-sm text-muted-foreground">
                    Aucune photo
                  </div>
                )}
                <label className="btn-ghost text-sm w-full cursor-pointer">
                  <Upload className="w-4 h-4" /> {uploading ? "Upload…" : (partner.image_url ? "Remplacer la photo" : "Uploader une photo")}
                  <input type="file" accept="image/*" className="hidden" data-testid="m-upload-input" onChange={(e) => onUpload(e.target.files?.[0])} />
                </label>
                <div className="text-[11px] text-muted-foreground leading-relaxed">JPEG, PNG — 5 Mo max. Format paysage conseillé (4:3).</div>
              </div>
            </Card>

            <Card title="Conseil">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Mettez en avant la valeur réelle en € (ex. "-5€" plutôt que "-10%").</li>
                <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Ajoutez une photo de qualité, lumineuse et authentique.</li>
                <li className="flex gap-2"><span className="text-lime-600 font-bold">•</span> Cliquez "Soumettre" une fois la fiche complète.</li>
              </ul>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="card-premium p-6 md:p-8">
      <h2 className="font-heading font-bold text-navy-900 text-lg mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-navy-900/70 font-semibold mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function PricingBanner() {
  return (
    <div className="rounded-2xl border border-lime-500/30 bg-gradient-to-br from-lime-500/15 via-white to-white p-6 md:p-8" data-testid="pricing-banner">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="flex gap-4">
          <div className="w-12 h-12 rounded-xl bg-lime-500 flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-navy-900" /></div>
          <div>
            <div className="inline-block chip bg-navy-900 text-lime-400 mb-1.5">Gratuit — self-service</div>
            <div className="font-heading font-bold text-navy-900 text-lg">Je publie moi-même mon offre</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Complétez votre fiche ici : infos du commerce, offre en €, photo. Cliquez sur "Soumettre" et l'équipe B.health® valide sous 48h. <span className="font-semibold text-navy-900">Aucun frais.</span>
            </p>
          </div>
        </div>
        <div className="flex gap-4 md:border-l md:border-navy-900/10 md:pl-6">
          <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center shrink-0"><Coins className="w-6 h-6 text-lime-400" /></div>
          <div>
            <div className="inline-block chip bg-lime-500 text-navy-900 mb-1.5">On le fait pour vous</div>
            <div className="font-heading font-bold text-navy-900 text-lg">Service rédaction & mise en ligne — 25€ HTVA</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Notre équipe rédige votre fiche, optimise votre photo et met en ligne à votre place. Facturation unique 25€ HTVA.
            </p>
            <SafeEmail
              user="sante"
              domain="bhealth.be"
              subject='Service "On le fait pour vous" - Local Benefits'
              className="inline-flex items-center gap-1 text-xs font-semibold text-navy-900 mt-2 hover:underline"
              testId="merchant-dashboard-contact"
            >
              <Info className="w-3 h-3 mr-1" /> Demander ce service
            </SafeEmail>
          </div>
        </div>
      </div>
    </div>
  );
}
